import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  connectLinear,
  listLinearInstallations,
} from '../../services/linearApi';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';
import { PAGE_SEO, SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateLinearIntegrationSchema, JsonLd } from '../../utils/structuredData';
import LinearHero from './sections/LinearHero';
import LinearProductHero from './sections/LinearProductHero';
import LinearHowItWorks from './sections/LinearHowItWorks';
import LinearEngineering from './sections/LinearEngineering';
import LinearForTeams from './sections/LinearForTeams';
import LinearFAQ from './sections/LinearFAQ';
import LinearClose from './sections/LinearClose';
import CrossDiscovery from '../OpenClaw/sections/CrossDiscovery';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import './LinearLandingPage.css';

/**
 * LinearLandingPage — the public marketing + first-connect page at cellcog.ai/linear.
 *
 * Reuses the existing Linear OAuth flow. Adaptive CTA:
 *  - logged-out  → signup with intent stashed via persistentNavigation
 *    (saveIntendedUrl) — the real post-auth routing rail; the old `?return=`
 *    query param on /signup was dead code (nothing read it). On return, the
 *    intent effect below auto-starts the OAuth flow the visitor came for.
 *  - logged-in + no installs → trigger connectLinear() → Linear OAuth redirect
 *  - logged-in + has installs → /delegate/linear (management)
 *
 * Lives at /linear — canonical, SEO-indexed, Linear Directory submission URL.
 * Management lives separately at /delegate/linear.
 *
 * Design direction: Apple-like vision-led hero, then real product hero screenshot,
 * then real-artifact proof, then experience walkthrough, then engineering pride,
 * then team/trust beat, then objection-handling FAQ, then vision close.
 */
const LinearLandingPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const [installations, setInstallations] = useState(null); // null = unknown, [] = none, [...] = has
  const [connecting, setConnecting] = useState(false);

  // Fetch existing Linear installations for logged-in users so the CTA can adapt.
  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setInstallations(null);
      return undefined;
    }
    listLinearInstallations()
      .then((resp) => {
        if (!cancelled) setInstallations(resp.data || []);
      })
      .catch((err) => {
        // Non-critical — CTA will fall back to "Connect Linear Workspace" behavior.
        console.error('Failed to fetch Linear installations:', err);
        if (!cancelled) setInstallations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Single connect handler, mounted in every CTA on the page.
  //
  // Rule: if the user isn't connected, START the OAuth flow immediately.
  // We never route visitors to the connectors page just to see a card —
  // visitors came here to connect, so we make connecting the default action.
  //
  // Logged-out users → signup flow that returns to /linear with intent preserved.
  // Logged-in users → initiate connectLinear(); backend always returns an auth_url
  //   that redirects to Linear's OAuth consent. Linear's own consent screen handles
  //   the workspace picker (new vs existing). The upsert happens in our callback
  //   handler — backend never blocks the connect flow based on prior installations.
  const handleConnect = useCallback(async (location) => {
    trackEvent('cta_click', {
      cta_name: 'connect_linear',
      location: typeof location === 'string' ? location : 'page',
      page: 'linear',
    });
    if (!currentUser) {
      saveIntendedUrl('/linear?intent=connect_linear');
      navigate('/signup');
      return;
    }
    try {
      setConnecting(true);
      const response = await connectLinear();
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to initiate Linear connection');
      console.error('Linear connection error:', error);
      setConnecting(false);
    }
  }, [currentUser, navigate]);

  // Post-signup auto-connect: consume ?intent=connect_linear after the visitor
  // lands back here from the signup flow, then start the OAuth they came for.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intent') === 'connect_linear') {
      // Cleanup URL before connecting so a refresh doesn't re-trigger
      params.delete('intent');
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.replaceState({}, document.title, url.toString());
      handleConnect('post_signup_auto');
    }
  }, [currentUser, handleConnect]);

  // CTA label adapts to auth state only — we always try to connect, so the
  // label reflects the action we're taking, not the post-connect state.
  const ctaLabel = (() => {
    if (!currentUser) return 'Sign up to connect';
    return 'Connect Linear Workspace';
  })();

  const seo = PAGE_SEO.linear;
  const canonicalUrl = `${SEO_DEFAULTS.url}/linear`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/linear/og-linear.png`;

  const handleSeeHowItWorks = () => {
    const target = document.getElementById('linear-how-it-works');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        {/* Inter Display — scoped to this page for Linear-aesthetic hero gravitas. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateLinearIntegrationSchema()} />

      <div className={`linear-landing ${isDarkMode ? 'dark' : 'light'}`}>
        <LinearHero
          ctaLabel={ctaLabel}
          onConnect={() => handleConnect('hero')}
          onSeeHowItWorks={handleSeeHowItWorks}
          connecting={connecting}
        />
        <LinearProductHero onConnect={() => handleConnect('product_hero')} />
        <LinearHowItWorks />
        <LinearEngineering />
        <LinearForTeams />
        <LinearFAQ />
        <CrossDiscovery currentPage="linear" />
        <LinearClose
          ctaLabel={ctaLabel}
          onConnect={() => handleConnect('close')}
          connecting={connecting}
        />
        <MobileStickyCta label={ctaLabel} onClick={() => handleConnect('sticky_bar')} />
      </div>
    </>
  );
};

export default LinearLandingPage;
